/**
 * Host integration hooks — pure helpers behind the widget's window-level entry points, kept free of
 * React/DOM so the dispatch logic is unit-testable (the component just wires them to listeners).
 *
 * A host page (or a desktop shell hosting a WebView) can drive the widget two equivalent ways:
 *   - `window.postMessage({ type: 'gyro:voice-toggle' })` / `window.postMessage({ type: 'gyro:open' })` /
 *     `window.postMessage({ type: 'gyro:close' })`
 *   - `window.dispatchEvent(new CustomEvent('gyro:voice-toggle'))` (and the 'gyro:open' and 'gyro:close' events)
 *
 * SECURITY: a `message` event can come from ANY window (embedded iframes, extensions, an opener) and
 * ANY origin. Two independent defences apply:
 *   1. `isTrustedHostHookEvent` gates on the EVENT: the sender must be this very window (`e.source ===
 *      window` — host hooks are a same-window contract; a cross-window sender is dropped silently), and
 *      when the host configured an origin allowlist, `e.origin` must be on it (review finding L-29).
 *   2. `parseHostHookMessage` treats the PAYLOAD as untrusted: only the `type` string is inspected, no
 *      other field is ever read, and unknown/malformed messages are ignored.
 * The actions themselves ("open", "toggle the mic") are things the user could do with one click anyway,
 * but a page that can be scripted into opening the mic from a hostile iframe is still a foothold — hence
 * the source check even for these low-risk actions.
 */
/**
 * Classify a `message` event's data. Returns the hook action, or `null` for anything that is not an
 * exact, well-formed Gyro hook message (wrong shape, wrong type string, primitives, null, arrays…).
 */
export function parseHostHookMessage(data) {
    if (typeof data !== 'object' || data === null)
        return null;
    const type = data.type;
    if (type === 'gyro:voice-toggle')
        return 'voice-toggle';
    if (type === 'gyro:open')
        return 'open';
    if (type === 'gyro:close')
        return 'close';
    if (type === 'gyro:sign-out')
        return 'sign-out';
    if (type === 'gyro:support-card')
        return 'support-card';
    if (type === 'gyro:compose')
        return 'compose';
    return null;
}
/** The longest text a `gyro:compose` hook may carry (the desktop shell enforces the same bound before it relays). */
export const COMPOSE_TEXT_MAX_CHARS = 200;
/** What a one-line composer prompt may never hold: line breaks, controls, bidirectional controls, invisible characters. */
const COMPOSE_REFUSED = /[\u0000-\u001f\u007f-\u009f\u00ad\u061c\u180e\u200b\u200e\u200f\u2028\u2029\u202a-\u202e\u2060-\u2064\u2066-\u2069\ufeff\ufff9-\ufffb]/;
/**
 * The text of a `gyro:compose` hook (a postMessage's data, or a CustomEvent's detail): a string of 1 to 200 characters on
 * one line, none of the refused characters. The payload is untrusted data (any script in the page can post it): anything
 * else is null, never trimmed into shape. Surrounding white space is dropped.
 */
export function parseComposeText(data) {
    if (typeof data !== 'object' || data === null)
        return null;
    const text = data.text;
    if (typeof text !== 'string' || text.length > COMPOSE_TEXT_MAX_CHARS || COMPOSE_REFUSED.test(text))
        return null;
    const trimmed = text.trim();
    return trimmed.length > 0 ? trimmed : null;
}
/** Normalize a configured origin: trim, drop a trailing slash, lower-case (origins are case-insensitive). */
function normalizeOrigin(o) {
    return o.trim().replace(/\/+$/, '').toLowerCase();
}
/**
 * Decide whether a `message` event may drive a host hook (L-29). Requirements, both mandatory:
 *   - `e.source === window` — same-window host hooks only. An iframe, an opener, an extension content
 *     script posting from another realm, or a `null` source is refused.
 *   - if `allowedOrigins` is configured (non-empty), `e.origin` must match one of them exactly.
 * Never throws; anything unexpected is "not trusted".
 */
export function isTrustedHostHookEvent(e, trust) {
    try {
        if (!e || !trust || trust.window === undefined || trust.window === null)
            return false;
        if (e.source !== trust.window)
            return false;
        const allow = (trust.allowedOrigins ?? []).map(normalizeOrigin).filter((o) => o.length > 0);
        if (allow.length === 0)
            return true;
        if (typeof e.origin !== 'string' || e.origin.length === 0)
            return false;
        return allow.includes(normalizeOrigin(e.origin));
    }
    catch {
        return false;
    }
}
/** Upper bound on a host-supplied situation prompt — a hook is not a channel for pasting a document. */
export const SITUATION_PROMPT_MAX_CHARS = 8000;
/**
 * Validate the `detail` of a `gyro:situation` CustomEvent (L-28). Returns the trimmed prompt, or `null`
 * when the detail is not `{ prompt: <non-empty string> }`. Over-long prompts are truncated, never
 * rejected, so a host that dumps a long stack trace still opens the widget on something useful.
 */
export function parseSituationPrompt(detail) {
    if (typeof detail !== 'object' || detail === null)
        return null;
    const prompt = detail.prompt;
    if (typeof prompt !== 'string')
        return null;
    const trimmed = prompt.trim();
    if (!trimmed)
        return null;
    return trimmed.length > SITUATION_PROMPT_MAX_CHARS ? trimmed.slice(0, SITUATION_PROMPT_MAX_CHARS) : trimmed;
}
/**
 * Re-entrancy latch for the voice toggle: opening a collapsed widget starts the mic only after the
 * panel has mounted (~200 ms), so a second trigger inside that window would race the first and leave
 * the session half-toggled. `tryEnter()` returns true and arms the latch, or false while a prior
 * trigger is still settling. Injectable clock for tests.
 */
export function createHookGate(settleMs = 400, now = Date.now) {
    let last = Number.NEGATIVE_INFINITY;
    return {
        tryEnter() {
            const t = now();
            if (t - last < settleMs)
                return false;
            last = t;
            return true;
        },
    };
}
//# sourceMappingURL=hostHooks.js.map